import { useCallback, useEffect, useRef, useState } from 'react';
import { getProject, pollScene } from '../../lib/aiStudioApi';

const STALE_MS = 10 * 60 * 1000;

export const jobRunning = (job) => job?.status === 'running';
export const jobStalled = (job) =>
  jobRunning(job) && Date.now() - new Date(job.updated_at || 0).getTime() > STALE_MS;
export const isRendering = (s) => ['processing', 'submitting'].includes(s.video_status) && !s.video_url;
export const finalCutRunning = (fc) => fc?.status === 'running' && Date.now() - new Date(fc.updated_at || 0).getTime() < 12 * 60 * 1000;

export function useMovieProject(id) {
  const [project, setProject] = useState(null);
  const idRef = useRef(id);
  idRef.current = id;

  const reload = useCallback(async () => {
    if (!id) { setProject(null); return null; }
    const p = await getProject(id);
    if (idRef.current === id) setProject(p);
    return p;
  }, [id]);

  useEffect(() => { setProject(null); reload().catch(() => {}); }, [reload]);

  useEffect(() => {
    if (!project) return undefined;
    const busy = [project.direction, project.production, project.voices, project.voices_upgrade].some((j) => jobRunning(j) && !jobStalled(j))
      || finalCutRunning(project.final_cut);
    const pending = (project.scenes || []).filter((s) => isRendering(s) && s.video_task_id);
    if (!busy && !pending.length) return undefined;
    const t = setTimeout(async () => {
      await Promise.allSettled(busy ? [] : pending.slice(0, 4).map((s) => pollScene(project.id, s.id)));
      reload().catch(() => {});
    }, busy ? 4000 : 9000);
    return () => clearTimeout(t);
  }, [project, reload]);

  return { project, reload };
}
